'use client';

import React, { FC, memo } from 'react';

import { GProduct, ProductCardSlice } from '@/types';
import { useData } from '@/components/DataProvider';
import {
  ProductCardBtnBottomHover,
  ProductCardDefault,
  ProductCardLarge,
  ProductCardTraditional,
  ProductCardWithBorder,
  ProductCardWithBtnHover,
  ProductCardWithHighlighted,
  ProductCardWithInfoCentered,
  ProductCardWithSmallBtn,
} from '@/components/Product/variations';
import { ProductProvider } from '@/components/Product/context';
import { useFeaturedFlag } from '@/hooks/useFeaturedFlag';

const Product: FC<IProductProps> = ({
  className,
  product,
  analyticsListName,
  slice,
}) => {
  const { productCard } = useData();
  const { isAddToCartInKioskDisabled } = useFeaturedFlag();
  const { kioskActiveProduct } = productCard;
  const { isLargeCard } = productCard.displayProps;
  // this will be used only when the component is used from the
  // slice simulator, so we can preview on the CMS with the
  // mocked data how the card will look like
  let activeSlice = kioskActiveProduct as ProductCardSlice;

  if (slice) activeSlice = slice;

  const Component =
    ComponentsByVariations[activeSlice?.variation ?? 'kioskDefault'];

  return (
    <ProductProvider product={product}>
      <Component
        analyticsListName={analyticsListName}
        className={className}
        isAddToCartInKioskDisabled={isAddToCartInKioskDisabled}
        isLargeCard={isLargeCard ?? false}
        onKiosk
      />
    </ProductProvider>
  );
};

type ProductVariation = ProductCardSlice['variation'] | 'kioskDefault';

const ComponentsByVariations: Record<
  ProductVariation,
  React.FC<ProductVariationProps>
> = {
  kioskDefault: ProductCardWithSmallBtn,
  default: ProductCardDefault,
  productCardWithSmallBtn: ProductCardWithSmallBtn,
  productCardWithBtnHover: ProductCardWithBtnHover,
  productCardWithHighlighted: ProductCardWithHighlighted,
  productCardWithBorder: ProductCardWithBorder,
  productCardWithInfoCenter: ProductCardWithInfoCentered,
  productCardLarge: ProductCardLarge,
  productCardTraditional: ProductCardTraditional,
  productCardBtnBottomHover: ProductCardBtnBottomHover,
};

export interface IProductProps {
  className?: string;
  product: GProduct;
  analyticsListName?: string;
  slice?: ProductCardSlice;
  onKiosk?: boolean;
  isAddToCartInKioskDisabled?: boolean;
}

export interface IProductCard extends IProductProps {
  isLargeCard: boolean;
}

export type ProductVariationProps = Omit<IProductCard, 'product'>;

export default memo(Product);
